import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom/client';
import 'semantic-ui-css/semantic.min.css';
import './assets/css/main.css';
import MainMenu from './components/Menus/Menu';
import logo from './assets/img/logo.svg';
import { ThemeProvider } from '@mui/material/styles';
import { SnackbarProvider, useSnackbar } from 'notistack';
import AppRoutes from './AppRoutes';
import { BrowserRouter as Router, useLocation, useSearchParams } from 'react-router-dom';
import Login from './components/Login/Login';
import { theme } from './Theme';
import Loader from 'elements/Loader';
import { CiDesktop } from "react-icons/ci";
import TopMenuUser from 'components/Menus/TopMenuUser';
import LinkWebflow from 'components/Companies/Webflow/Link';
import { ApiRequest } from 'GlobalFunctions';
import TopMenuAdmin from 'components/Menus/TopMenuAdmin';
import Oauth from 'components/Oauth/Oauth';
import OauthAdmin from 'components/Admin/Oauth/Oauth';

import LoginV2 from 'components/LoginV2/Login';
import Checklist from 'components/Companies/OnboardingChecklist/Checklist';
import WelcomePopup from 'components/Companies/WelcomePopup/WelcomePopup';
import EmailConfirmation from 'components/LoginV2/EmailConfirmation';
import FloatingVideo from 'elements/FloatingVideo/FloatingVideo';
import { IntercomProvider, useIntercom } from 'react-use-intercom';
import io from 'socket.io-client';
import { ProjectConfig } from 'Global';

const Site = () => {
  const userIsLogged = localStorage.getItem('ltkn') !== null;
  
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const isHome = location.pathname === "/" ? "home" : "";
  const [isMobile, setIsMobile] = useState(window.innerWidth < 700);
  const [showDifferentSiteMessage, setShowDifferentSiteMessage] = useState(false);

  // Check if current path is email verification
  const isEmailVerificationPath = location.pathname === '/email-confirmation';

  useEffect(() => {
    // Check for the webflow open parameter
    const openWebflow = searchParams.get('openWebflow');
    const webflowUrl = searchParams.get('webflowUrl');
    
    if (openWebflow === 'true' && webflowUrl) {
      // Open Webflow in a new tab
      window.open(decodeURIComponent(webflowUrl), '_blank');
      
      // Clear the parameters to avoid reopening the tab on refreshes
      searchParams.delete('openWebflow');
      searchParams.delete('webflowUrl');
      setSearchParams(searchParams);
    }

    // Check for different_site parameter
    const differentSite = searchParams.get('show_different_site');
    if (differentSite === 'true') {
      setShowDifferentSiteMessage(true);
      
      // Clear the parameter to avoid showing the message again on refreshes
      searchParams.delete('different_site');
      setSearchParams(searchParams);
    }
    
    const handleResize = () => {
      setIsMobile(window.innerWidth < 700);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [searchParams, setSearchParams]);

  const storeAlreadyLinked = localStorage.getItem('store_id') !== null;
  const role_id = localStorage.getItem('role_id') || "0";

  const [companyData, setCompanyData] = useState<any>({
    logo: '',
  });
  
  const [userData, setUserData] = useState<any>({
    image: '',
    name: '',
    lastname: '',
  });

  // Socket.IO setup for store transfer notifications
  useEffect(() => {
    if (!userIsLogged) return;
    
    const storeId = localStorage.getItem('store_id');
    if (!storeId) return;

    const API_URL = ProjectConfig.api_url;
    const socket = io(API_URL);

    // Register with store ID for targeted notifications
    socket.on('connect', () => {
      socket.emit('register', storeId);
      console.log('Socket connected for store:', storeId);
    });

    // Listen for store transfer completion events
    socket.on('store-transfer-completed', (data: any) => {
      console.log('Store transfer completed:', data);
      // Remove store_id from localStorage
      localStorage.removeItem('store_id');
      // Reload the page to redirect user
      window.location.reload();
    });

    socket.on('disconnect', () => {
      console.log('Socket disconnected');
    });

    // Cleanup on unmount
    return () => {
      socket.disconnect();
    };
  }, [userIsLogged]);

  useEffect(() => {
    if (userIsLogged) {
      ValidateTokenAndGetUserInfo();
    }
  }, []);

  const ValidateTokenAndGetUserInfo = async () => {
    // First validate the token
    const tokenValidation = await ApiRequest({
      url: '/users/validate_token',
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      setResponse: (response: any) => {
        if (response.status === 'error') {
          // Token is invalid, clear all localStorage and reload
          localStorage.clear();
          window.location.reload();
          return;
        }
        // Token is valid, proceed to get user info
        GetUserInfo();
      }
    });
  };

  const GetUserInfo = async () => {
    if(!userIsLogged) return;
    const userInfo = await ApiRequest({
        url: '/users/getUserData',
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
        setResponse: (response: any) => {
          if(response.role_id === 1) {
            setCompanyData(response.data);
          }
          else {
            setUserData(response.data);
          }

          
            
        }
    });
  }



  const [open, setOpen] = useState(true);

  return (
    <ThemeProvider theme={theme}>
      <SnackbarProvider maxSnack={3}>
        {showDifferentSiteMessage && <DifferentSiteNotification />}
        {isMobile ? (
          <div className='noMobile'>
            <div className='noMobileContent'>
              <CiDesktop size={80} />
              <h2>Please use a desktop device to access this site</h2>
              <span>Pennicart needs a bigger screen to work properly</span>
            </div>
          </div>
        ) : (
          // Allow email verification route without login
          isEmailVerificationPath ? (
            <EmailConfirmation />
          ) : !userIsLogged ? (
            <LoginV2 setLoading={setLoading} />
          ) : (
            (Number(role_id) < 100 && location.pathname === '/webflow') ? (
              <LinkWebflow setLoading={setLoading} new_store={!storeAlreadyLinked} />
            ) : Number(role_id) < 100 && location.pathname === '/oauth' ? (
              <Oauth setLoading={setLoading} />
            ) : Number(role_id) === 100 && location.pathname === '/admin/oauth' ? (
              <OauthAdmin setLoading={setLoading} />
            ) : (
              <div id='structure'>
                <div id="main-container">
                  <div id='header_container'>
                    <header data-pc="nav-component" className="nav_padding">
                      <div className="nav_container">
                        <div className="nav_logo w-embed">
                          <a href='/'><img src={logo} alt="Logo"></img></a>
                        </div>
                        {Number(role_id) < 100 ?
                          <TopMenuUser setLoading={setLoading} data={companyData}/>
                          :
                          <TopMenuAdmin setLoading={setLoading} data={userData}/>
                        }

                      </div>
                    </header>
                    <aside id="menu">
                      <MainMenu setLoading={setLoading} />
                    </aside>
                  </div>
                  <div id="main-content">
                    <Loader loading={loading} />
                    <div id='content'>
                      <AppRoutes setLoading={setLoading} GetUserInfo={GetUserInfo}/>
                      {Number(role_id) < 100 && (
                        <>
                         
                          {storeAlreadyLinked && <Checklist />}
                          {storeAlreadyLinked && <WelcomePopup />}
                          {storeAlreadyLinked && <FloatingVideo />}
                          <IntercomProvider
                            appId="bctzes5k"
                            autoBoot
                          />
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )
          )
        )}
      </SnackbarProvider>
    </ThemeProvider>
  );
};

// Component to display the notification
const DifferentSiteNotification = () => {
  const { enqueueSnackbar } = useSnackbar();
  
  useEffect(() => {
    enqueueSnackbar("You're trying to connect a new store. Please use the profile dropdown to add it", { 
      variant: 'error',
      autoHideDuration: 6000
    });
  }, [enqueueSnackbar]);
  
  return null;
};

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
root.render(

    <Router>
      <Site />
    </Router>
);
